import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Import key assets
import oldPoster from '../assets/old-poster.gif';
import tezosPoster from '../assets/tezos-poster.webp';
import skins from '../assets/skins.jpg';

// Import FoundersSection component
import FoundersSection from '../components/FoundersSection';

// Import analytics hook
import { useAnalyticsSummary } from '../hooks/useAnalyticsSummary';

// Import selected NFT people images for showcase
import nft1 from '../assets/people/nftpeople-1.png';
import nft2018 from '../assets/people/nftpeople-2018.png';
import nft2518 from '../assets/people/nftpeople-2518.png';
import nft2702 from '../assets/people/nftpeople-2702.png';
import nft3377 from '../assets/people/nftpeople-3377.png';
import nft5000 from '../assets/people/nftpeople-5000.png';

const showcaseNFTs = [nft1, nft2018, nft2518, nft2702, nft3377, nft5000];

function Home() {
  const { data: analyticsData, isLoading: analyticsLoading, isError: analyticsError } = useAnalyticsSummary();

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              <span className="pixel-text">NFT</span>
              <span className="pixel-text-alt">-People</span>
            </h1>
            <p className="hero-subtitle">The First Generative Pixel Art PFP Collection on Tezos</p>
            <p className="hero-description">
              In June 2021, we became acquainted with the Tezos blockchain and launched the first profile picture generative project. 
              As newcomers to NFTs, we designed a robot to mint 5000 unique avatars, each with 408 possible traits.
            </p>
            <div className="hero-cta">
              <Link to="/rarity" className="primary-btn pixel-btn">
                <span className="btn-text">Explore Rarity</span>
                <span className="btn-pixel">⚡</span>
              </Link>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-poster">
              <img src={oldPoster} alt="NFT-People Original Poster" className="poster-image" />
            </div>
            <div className="hero-nfts">
              {showcaseNFTs.slice(0, 4).map((img, index) => (
                <div key={index} className="hero-nft-item">
                  <img src={img} alt={`NFT People ${index + 1}`} className="hero-nft-img" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Analytics Summary Section */}
      <section className="analytics-summary-section">
        <div className="analytics-summary-container">
          <div className="analytics-summary-header">
            <h2 className="analytics-summary-title">Collection Analytics</h2>
            <p className="analytics-summary-subtitle">
              Real-time insights into the NFT-People collection performance and market data.
            </p>
          </div>
          
          <div className="analytics-summary-grid">
            <div className="analytics-summary-card">
              <h3 className="analytics-card-title">Items</h3>
              <div className="analytics-card-metrics">
                <div className="metric-value">
                  {analyticsLoading ? 'Loading' : formatNumber(analyticsData?.items || 0)}
                </div>
                <div className="metric-subtitle">Total Collection</div>
              </div>
            </div>
            
            <div className="analytics-summary-card">
              <h3 className="analytics-card-title">Floor Price</h3>
              <div className="analytics-card-metrics">
                <div className="metric-value">
                  {analyticsLoading ? 'Loading' : `${analyticsData?.floorPrice || '0'} XTZ`}
                </div>
                <div className="metric-subtitle">Current Floor</div>
              </div>
            </div>
            
            <div className="analytics-summary-card">
              <h3 className="analytics-card-title">Total Volume</h3>
              <div className="analytics-card-metrics">
                <div className="metric-value">
                  {analyticsLoading ? 'Loading' : `${analyticsData?.totalVolume || '0'} XTZ`}
                </div>
                <div className="metric-subtitle">All Time</div>
              </div>
            </div>
            
            <div className="analytics-summary-card">
              <h3 className="analytics-card-title">24h Volume</h3>
              <div className="analytics-card-metrics">
                <div className="metric-value">
                  {analyticsLoading ? 'Loading' : `${analyticsData?.volume24h || '0'} XTZ`}
                </div>
                <div className="metric-subtitle">Last 24 Hours</div>
              </div>
            </div>
            
            <div className="analytics-summary-card">
              <h3 className="analytics-card-title">Owners</h3>
              <div className="analytics-card-metrics">
                <div className="metric-value">
                  {analyticsLoading ? 'Loading' : formatNumber(analyticsData?.owners || 0)}
                </div>
                <div className="metric-subtitle">Unique Wallets</div>
              </div>
            </div>
            
            <div className="analytics-summary-card">
              <h3 className="analytics-card-title">For Sale</h3>
              <div className="analytics-card-metrics">
                <div className="metric-value">
                  {analyticsLoading ? 'Loading' : formatNumber(analyticsData?.forSale || 0)}
                </div>
                <div className="metric-subtitle">Active Listings</div>
              </div>
            </div>
            
            <div className="analytics-summary-card">
              <h3 className="analytics-card-title">Avg Price</h3>
              <div className="analytics-card-metrics">
                <div className="metric-value">
                  {analyticsLoading ? 'Loading' : `${analyticsData?.avgPrice || '0'} XTZ`}
                </div>
                <div className="metric-subtitle">Average Sale</div>
              </div>
            </div>
            
            <div className="analytics-summary-card">
              <h3 className="analytics-card-title">24h Sales</h3>
              <div className="analytics-card-metrics">
                <div className="metric-value">
                  {analyticsLoading ? 'Loading' : formatNumber(analyticsData?.sales24h || 0)}
                </div>
                <div className="metric-subtitle">Transactions</div>
              </div>
            </div>
          </div>
          
          <div className="analytics-summary-cta">
            <Link to="/analytics" className="analytics-summary-btn">
              <span className="btn-text">View Full Analytics</span>
              <span className="btn-icon">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="story-section">
        <div className="story-container">
          <div className="story-header">
            <h2 className="story-title">Our Journey</h2>
            <p className="story-subtitle">From 5000 unique avatars to a thriving community</p>
          </div>
          
          <div className="story-content">
            <div className="story-card main-story">
              <div className="story-image">
                <img src={tezosPoster} alt="Tezos NFT-People" className="story-img" />
              </div>
              <div className="story-text">
                <h3>The First Generative PFP on Tezos</h3>
                <p>
                  It was in June 2021 when we first became acquainted with the Tezos blockchain, and shortly after, 
                  the website launched as an independent platform where anyone could freely sell their art.
                </p>
                <p>
                  As newcomers to the world of NFTs, we were eager to enter this attractive space quickly. 
                  Although I had not yet learned how to write a smart contract, we decided to proceed by designing 
                  a robot that could mint 5000 avatars, which were linked to the hicetnunc.xyz website.
                </p>
              </div>
            </div>

            <div className="story-grid">
              <div className="story-card">
                <div className="story-icon">🎨</div>
                <h3>408 Unique Traits</h3>
                <p>Each NFT-People is unique and programmatically generated from 408 possible traits, including:</p>
                <ul className="trait-list">
                  <li>42 backgrounds</li>
                  <li>102 expressions of eyes and glasses</li>
                  <li>100 headwear and hair</li>
                  <li>41 clothing</li>
                  <li>86 expressions of mouth</li>
                  <li>37 noses</li>
                </ul>
              </div>
              
              <div className="story-card">
                <div className="story-icon">💰</div>
                <h3>Equal Opportunity</h3>
                <p>
                  We started with zero advertising, even among our friends. We set the price for all at 1 Tezos, 
                  allowing everyone to have an equal chance to own a unique NFT-People.
                </p>
                <p>
                  We are thrilled that all 5000 of them were sold out, and we are grateful to everyone who supported us on this journey.
                </p>
              </div>
              
              <div className="story-card">
                <div className="story-icon">🔍</div>
                <h3>Rarity Tool</h3>
                <p>
                  After the minting of the 3000th avatar, we asked those who had purchased it for the rarities list. 
                  To create the avatars, my co-worker, an artist, drew all the single assets as PNG format, 
                  and I wrote only a few lines of code to merge them.
                </p>
                <p>
                  I created a rarity tool that can sort the avatars based on their score and filter them based on those for sale.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Hidden */}
      {/* <section className="stats-section">
        <div className="stats-container">
          <h2 className="stats-title">Project Statistics</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">5000</div>
            <div className="stat-label">Total Supply</div>
          </div>
          <div className="stat-card">
              <div className="stat-number">5000</div>
              <div className="stat-label">Sold Out</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">532</div>
            <div className="stat-label">Collectors</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">6</div>
            <div className="stat-label">Layers</div>
          </div>
          <div className="stat-card">
              <div className="stat-number">408</div>
              <div className="stat-label">Total Traits</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">∞</div>
            <div className="stat-label">Possibilities</div>
          </div>
        </div>
        </div>
      </section> */}

      {/* Technical Section */}
      <section className="technical-section">
        <div className="technical-container">
          <div className="technical-content">
            <div className="technical-text">
              <h2>Technical Innovation</h2>
              <p>
                We sell collectibles, and it is essential to us that people who buy NFTs from us are engaged 
                with the feeling behind them. We sell something that you can use to create little stories in your mind 
                and add more meaning and value to a PNG file.
              </p>
              <p>
                Looking back on the journey, I realized that creating NFT-People was not just about minting avatars 
                or making money, but it was also about building a community and being a part of something new and exciting. 
                It was about learning and exploring the possibilities of Blockchain technology and NFTs.
              </p>
          </div>
            <div className="technical-visual">
              <img src={skins} alt="NFT-People Skins" className="technical-img" />
              </div>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <FoundersSection />

      {/* Community Section */}
      <section className="community-section">
        <div className="community-container">
          <h2>Building the Future</h2>
          <p>
            Our project had proven that NFTs were more than just digital collectibles; they were a platform for 
            creative expression and a new form of art. They could represent ideas, emotions, and stories, 
            and they could connect people around the world.
          </p>
          <p>
            As we move forward, we continue to explore new ways of creating and sharing NFTs, and we hope to inspire 
            others to join us on this journey. We invite you to explore our NFT-People collection and discover the 
            rarest pixel art avatars on Tezos.
          </p>
          <div className="community-cta">
              <Link to="/rarity" className="primary-btn pixel-btn">
                <span className="btn-text">Discover Rarity</span>
                <span className="btn-pixel">🔍</span>
              </Link>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3 className="footer-title">NFT-People</h3>
              <p className="footer-description">
                The First Generative Pixel Art PFP Collection on Tezos Blockchain. 
                Discover 5000 unique avatars with 408 possible traits.
              </p>
              <div className="footer-stats">
                <div className="footer-stat">
                  <span className="stat-number">5000</span>
                  <span className="stat-label">Unique NFTs</span>
                </div>
                <div className="footer-stat">
                  <span className="stat-number">408</span>
                  <span className="stat-label">Traits</span>
                </div>
                <div className="footer-stat">
                  <span className="stat-number">2021</span>
                  <span className="stat-label">Founded</span>
                </div>
              </div>
            </div>
            
            <div className="footer-links">
              {/* <div className="footer-section">
                <h4 className="section-title">Collection</h4>
                <ul className="footer-menu">
                  <li><Link to="/rarity" className="footer-link">Rarity Tool</Link></li>
                  <li><a href="https://objkt.com/collection/hicetnunc" target="_blank" rel="noopener noreferrer" className="footer-link">View on Objkt</a></li>
                  <li><a href="https://tezos.art/collection/hicetnunc" target="_blank" rel="noopener noreferrer" className="footer-link">Tezos.art</a></li>
                </ul>
              </div> */}
              
              <div className="footer-section">
                <h4 className="section-title">Community</h4>
              <div className="social-links">
                  <a href="https://discord.gg/hXVgfeXaXB" target="_blank" rel="noopener noreferrer" className="social-link discord">
                    <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5499-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0189 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z"/>
                    </svg>
                  <span className="social-text">Discord</span>
                </a>
                  <a href="https://x.com/PeopleNft" target="_blank" rel="noopener noreferrer" className="social-link twitter">
                    <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  <span className="social-text">Twitter</span>
                </a>
          
                </div>
              </div>
            </div>
        </div>
          
          <div className="footer-bottom">
            <div className="footer-bottom-content">
              <p className="footer-copyright">© 2025 NFT-People. All rights reserved.</p>
              {/* <div className="footer-legal">
                <a href="#" className="legal-link">Privacy Policy</a>
                <span className="separator">•</span>
                <a href="#" className="legal-link">Terms of Service</a>
              </div> */}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;


